import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Clock, Upload, FileText, Download, ShieldCheck, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const STEP_META = {
  proof_of_product: {
    label: 'Proof of Product',
    owner: 'seller',
    hint: 'Seller uploads a document (assay, mine license, inventory photo) confirming physical possession of the goods.',
  },
  proof_of_funds: {
    label: 'Proof of Funds',
    owner: 'buyer',
    hint: 'Buyer uploads a bank comfort letter or account statement (dated within 30 days) confirming ability to pay.',
  },
  spa_execution: {
    label: 'SPA Execution',
    owner: 'both',
    hint: 'Sales & Purchase Agreement signed by both parties. Tracked automatically from the SPA flow.',
  },
};

const STATUS_META = {
  pending:     { label: 'Pending',     Icon: Clock,         cls: 'text-[#94A3B8] bg-white/5 border-[#1E293B]' },
  uploaded:    { label: 'Uploaded',    Icon: FileText,      cls: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/40' },
  in_progress: { label: 'In Progress', Icon: Clock,         cls: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/40' },
  verified:    { label: 'Verified',    Icon: CheckCircle2,  cls: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/40' },
  rejected:    { label: 'Rejected',    Icon: XCircle,       cls: 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/40' },
};

/**
 * Transaction Checklist card — rendered on DealDetail once an FCO is acknowledged.
 * Props:
 *   dealId, viewerRole, canUpload (map: proof_of_product bool, proof_of_funds bool)
 */
export default function TransactionChecklist({ dealId, viewerRole, onChanged }) {
  const { getAuthHeader } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingStep, setUploadingStep] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/deals/${dealId}/checklist`, { headers: getAuthHeader() });
      setChecklist(r.data);
      onChanged?.(r.data);
    } catch (err) {
      // Silent — checklist just doesn't exist yet (before FCO ack).
      setChecklist(null);
      onChanged?.(null);
    } finally { setLoading(false); }
  }, [dealId, getAuthHeader, onChanged]);
  useEffect(() => { load(); }, [load]);

  const canUploadForStep = (step) => {
    if (step === 'proof_of_product') return viewerRole === 'seller';
    if (step === 'proof_of_funds')   return viewerRole === 'buyer';
    return false;
  };

  const upload = async (step, file) => {
    if (!file) return;
    setUploadingStep(step);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API_URL}/deals/${dealId}/checklist/${step}/upload`, fd,
        { headers: { ...getAuthHeader() } },
      );
      setChecklist(r.data);
      onChanged?.(r.data);
      toast.success(`${STEP_META[step].label} uploaded`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Upload failed');
    } finally { setUploadingStep(null); }
  };

  const downloadDoc = async (step, filename) => {
    try {
      const r = await axios.get(
        `${API_URL}/deals/${dealId}/checklist/${step}/download`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || `${step}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  if (loading) return null;
  if (!checklist) return null;  // Not yet created — FCO not acknowledged

  const steps = ['proof_of_product', 'proof_of_funds', 'spa_execution'];

  return (
    <Card className="bg-[#121A2F] border-[#1E293B] p-6 mt-4" data-testid="transaction-checklist-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#F59E0B]" />
          <h3 className="text-lg text-white font-semibold">Transaction Checklist</h3>
        </div>
        <span className="text-xs text-[#94A3B8]"
              data-testid="checklist-progress">
          {checklist.percent_complete}% complete
        </span>
      </div>
      <p className="text-xs text-[#64748B] mb-4">
        Once every step is verified, the deal is ready for payment execution.
      </p>

      <div className="space-y-3">
        {steps.map((step) => {
          const st = checklist.steps[step] || { status: 'pending' };
          const meta = STEP_META[step];
          const smeta = STATUS_META[st.status] || STATUS_META.pending;
          const Icon = smeta.Icon;
          const canUpload = canUploadForStep(step) && (st.status === 'pending' || st.status === 'rejected');
          return (
            <div key={step} className="border border-[#1E293B] rounded-lg p-4 bg-white/[0.02]"
                 data-testid={`checklist-step-${step}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white font-medium text-sm">{meta.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{meta.hint}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${smeta.cls}`}
                      data-testid={`checklist-status-${step}`}>
                  <Icon className="w-3.5 h-3.5" />{smeta.label}
                </span>
              </div>

              {st.file_name && (
                <div className="flex items-center gap-2 mt-3 text-xs text-[#94A3B8]">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="truncate">{st.file_name}</span>
                  <Button size="sm" variant="outline"
                          onClick={() => downloadDoc(step, st.file_name)}
                          data-testid={`checklist-download-${step}`}
                          className="ml-auto bg-transparent border-[#1E293B] text-white h-7 px-2 text-xs">
                    <Download className="w-3 h-3 mr-1" />Download
                  </Button>
                </div>
              )}

              {st.status === 'rejected' && st.rejection_reason && (
                <div className="mt-2 text-xs rounded-md border border-[#EF4444]/30 bg-[#EF4444]/10 text-[#FCA5A5] px-2 py-1.5"
                     data-testid={`checklist-rejection-${step}`}>
                  Rejected: {st.rejection_reason}
                </div>
              )}
              {st.status === 'verified' && st.notes && (
                <div className="mt-2 text-xs text-[#10B981]">Admin note: {st.notes}</div>
              )}
              {step === 'spa_execution' && st.notes && st.status !== 'verified' && (
                <div className="mt-2 text-xs text-[#94A3B8]">{st.notes}</div>
              )}

              {canUpload && (
                <div className="mt-3 flex gap-2">
                  <input type="file" accept=".pdf,image/png,image/jpeg,image/webp"
                         ref={(el) => (inputRefs.current[step] = el)}
                         onChange={(e) => upload(step, e.target.files?.[0])}
                         data-testid={`checklist-file-input-${step}`}
                         className="hidden" />
                  <Button size="sm"
                          onClick={() => inputRefs.current[step]?.click()}
                          disabled={uploadingStep === step}
                          data-testid={`checklist-upload-${step}`}
                          className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">
                    {uploadingStep === step
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Uploading…</>
                      : <><Upload className="w-3.5 h-3.5 mr-1" />{st.status === 'rejected' ? 'Re-upload' : 'Upload document'}</>}
                  </Button>
                  <span className="text-xs text-[#64748B] self-center">PDF / PNG / JPG · max 8 MB</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {viewerRole !== 'admin' && (
        <div className="flex justify-end mt-4">
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="checklist-refresh"
                  className="bg-transparent border-[#1E293B] text-white h-8 text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
          </Button>
        </div>
      )}
    </Card>
  );
}
